import { useEffect, useRef, useState } from 'react';

/**
 * Observer hook that provides two states:
 * - hasIntersected: Updated one time, once the element intersects with the viewport the first time (for animations).
 * - shouldTrackView: Updated one time, once the element intersects with the viewport and stays intersecting for a certain amount of time (for analytics).
 * @param ref the element to observe
 * @param intersectionThreshold the number of pixels to consider the element intersecting with the viewport
 * @returns [hasIntersected, shouldTrackView]
 */

export default function useIntersectionObserver(ref: React.RefObject<HTMLElement | null>, intersectionThreshold = 200) {
  const viewEventThreshold = 500;

  const hasTrackedView = useRef(false);
  const [shouldTrackView, setShouldTrackView] = useState(false);

  // currentIntersectionStatusRef represents the most recent value provided by the IntersectionObserver.
  // isIntersecting triggers a useEffect that checks the value of currentIntersectionStatusRef after a given delay.
  const currentIntersectionStatusRef = useRef(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          currentIntersectionStatusRef.current = entry.isIntersecting;
          if (!isIntersecting && entry.isIntersecting) {
            setIsIntersecting(entry.isIntersecting);
          } else if (isIntersecting && !entry.isIntersecting) {
            setIsIntersecting(false);
          }
        });
      },
      { rootMargin: `-${intersectionThreshold}px 0px -${intersectionThreshold}px 0px` }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isIntersecting, ref]);

  useEffect(() => {
    if (isIntersecting && !hasTrackedView.current) {
      setTimeout(() => {
        if (currentIntersectionStatusRef.current) {
          setShouldTrackView(true);
          hasTrackedView.current = true;
        }
      }, viewEventThreshold);
    }
  }, [isIntersecting]);

  useEffect(() => {
    if (!hasIntersected && isIntersecting) {
      setHasIntersected(true);
    }
  }, [hasIntersected, isIntersecting]);

  return [hasIntersected, shouldTrackView];
}
